import * as LabelPrimitive from "@radix-ui/react-label";
import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import { Controller, ControllerProps, FieldPath, FieldValues, FormProvider, useFormContext } from "react-hook-form";

import { cn } from "../../../utils";
import { ErrorAlert } from "../../shared/InlineError";
import { Button, TButtonProps } from "../../ui/Button";
import { Label } from "../Label";

import { Check, LoaderCircle, TriangleAlert } from "lucide-react";

const Form = FormProvider;

type TFormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<TFormFieldContextValue>({} as TFormFieldContextValue);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

type TFormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<TFormItemContextValue>({} as TFormItemContextValue);

const FormItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const id = React.useId();

    return (
      <FormItemContext.Provider value={{ id }}>
        <div
          ref={ref}
          className={cn("space-y-1", className)}
          {...props}
        />
      </FormItemContext.Provider>
    );
  }
);
FormItem.displayName = "FormItem";

const FormLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
  const { error, formItemId } = useFormField();

  return (
    <Label
      ref={ref}
      className={cn(error && "text-destructive", className)}
      htmlFor={formItemId}
      {...props}
    />
  );
});
FormLabel.displayName = "FormLabel";

const FormControl = React.forwardRef<React.ElementRef<typeof Slot>, React.ComponentPropsWithoutRef<typeof Slot>>(
  ({ ...props }, ref) => {
    const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

    return (
      <Slot
        ref={ref}
        id={formItemId}
        aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
        aria-invalid={!!error}
        {...props}
      />
    );
  }
);
FormControl.displayName = "FormControl";

const FormDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => {
    const { formDescriptionId } = useFormField();

    return (
      <p
        ref={ref}
        id={formDescriptionId}
        className={cn("text-sm text-muted-foreground", className)}
        {...props}
      />
    );
  }
);
FormDescription.displayName = "FormDescription";

const FormMessage = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, children, ...props }, ref) => {
    const { error, formMessageId } = useFormField();
    const body = error ? String(error?.message) : children;

    if (!body) {
      return null;
    }

    return (
      <p
        ref={ref}
        id={formMessageId}
        className={cn("text-sm font-medium text-destructive", className)}
        role="alert"
        {...props}
      >
        {body}
      </p>
    );
  }
);
FormMessage.displayName = "FormMessage";

/**
 * Renders the form-level (`root`) error as a global banner. Drop it anywhere
 * inside a `<Form>` — it reads the error from context, so no props are required.
 * `handleFormError` sets `root` for every error that isn't tied to a field
 * (invalid credentials, expired token, rate limiting, server/network failures).
 */
const FormRootError = ({ className }: { className?: string }) => {
  const {
    formState: { errors },
  } = useFormContext();
  const message = errors.root?.message;

  if (!message || typeof message !== "string") {
    return null;
  }

  return (
    <ErrorAlert
      variant="banner"
      message={message}
      className={className}
    />
  );
};
FormRootError.displayName = "FormRootError";

type TFormButtonProps = TButtonProps & { isLoading?: boolean; isSuccess?: boolean; isError?: boolean };

const FormButton = React.forwardRef<HTMLButtonElement, TFormButtonProps>(
  ({ isSuccess, isLoading, isError, children, className, ...props }, ref) => {
    let icon: null | React.ReactElement = null;
    if (isError) {
      icon = <TriangleAlert className="h-4 w-4" />;
    } else if (isSuccess) {
      icon = <Check className="h-4 w-4" />;
    } else if (isLoading) {
      icon = <LoaderCircle className={`h-4 w-4 animate-spin`} />;
    }

    return (
      <Button
        ref={ref}
        type="submit"
        className={cn("gap-1.5", className)}
        variant={isError ? "destructive" : "default"}
        {...props}
      >
        {icon}
        {children}
      </Button>
    );
  }
);

FormButton.displayName = "FormButton";

export {
  Form,
  FormButton,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormRootError,
  // eslint-disable-next-line react-refresh/only-export-components
  useFormField,
};
